import { ChevronRight } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { StatusChip } from '@/components/ui/status-chip';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { ApiError } from '@/lib/api/client';
import { useEngagementRequirementsQuery } from '@/lib/api/engagement-requirements';
import { useSuggestionsQuery } from '@/lib/api/discovery-suggestions';
import { useSignoffQuery } from '@/lib/api/requirements-signoff';
import {
  headerSummary,
  headerSummaryText,
} from '@/components/engagement-requirements/requirements-logic';
import { signoffHeadline } from '@/components/discovery/signoff/signoff-logic';
import { openSuggestionCount, requirementSourceCounts } from './module-status-logic';
import { STATUS_ROW_LINK, StatusRow } from './status-card-row';

/**
 * Selection status on the engagement Overview: the requirements by where
 * they came from and by criticality, the suggestions waiting and the
 * sign-off and packet state, each with its source, and a way into
 * Selection.
 *
 * Shown only when the engagement's Selection module is on. The requirement
 * rows are the same full list the Selection screen holds (one response, no
 * paging), so the figures are counted over them here, as the screen's own
 * header does. Suggestions and sign-off exist only with Discovery on and
 * started (the API answers not_found, then 409 `discovery.not_started`,
 * otherwise), so those two rows are asked for only then and say so before.
 * Layout follows `HealthPostureGrid` and the rows `StatusRow`, like the
 * Discovery card beside it.
 */
export function SelectionStatusCard({
  engagementId,
  discoveryOn,
  discoveryStarted = null,
}: {
  engagementId: string;
  /** Discovery is on too: suggestions and sign-off apply. */
  discoveryOn: boolean;
  /** Discovery has started (from its summary); null while that is not known yet. */
  discoveryStarted?: boolean | null;
}) {
  const discoveryReady = discoveryOn && discoveryStarted === true;
  const requirements = useEngagementRequirementsQuery(engagementId);
  const suggestions = useSuggestionsQuery(engagementId, discoveryReady);
  const signoff = useSignoffQuery(engagementId, discoveryReady);

  const rows = requirements.data?.items ?? [];
  const live = rows.filter((r) => r.status !== 'removed');
  const counts = requirementSourceCounts(rows);
  const open = openSuggestionCount(suggestions.data);
  const params = { engagementId };
  const n = (v: number) => v.toLocaleString('en-US');
  const bySource = [
    `Library ${n(counts.library)}`,
    `Imported ${n(counts.imported)}`,
    `Manual ${n(counts.manual)}`,
    ...(discoveryOn || counts.discovery > 0 ? [`Discovery ${n(counts.discovery)}`] : []),
  ].join(' · ');

  return (
    <section
      className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
      aria-labelledby="selection-status-title"
      data-testid="selection-status-card"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 id="selection-status-title" className="t-h3">
            Selection · requirements
          </h2>
          <p className="t-meta">
            {requirements.data
              ? headerSummaryText(headerSummary(live))
              : 'The client’s requirements, each with a criticality and a citation'}
          </p>
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/engagements/$engagementId/selection" params={params}>
            Open Selection
            <ChevronRight className="size-3" aria-hidden="true" />
          </Link>
        </Button>
      </div>

      {requirements.error ? (
        requirements.error instanceof ApiError && requirements.error.code === 'not_found' ? (
          <p className="text-body-sm text-ink-500">
            Selection is not available here: the module is off for this engagement, or you do not
            have access to it.
          </p>
        ) : (
          <ErrorMessage error={requirements.error} />
        )
      ) : !requirements.data ? (
        <p className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading requirements…
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <StatusRow
            label="Requirements by source"
            value={n(counts.total)}
            meta={
              counts.total === 0
                ? 'None yet. Load them from the library, import a spreadsheet or write one by hand.'
                : bySource
            }
            source="From each requirement’s citation"
            action={
              <Link
                to="/engagements/$engagementId/selection"
                params={params}
                className={STATUS_ROW_LINK}
              >
                Requirements
              </Link>
            }
          />
          <StatusRow
            label="Criticality"
            value={`${n(counts.critical)} critical`}
            meta={`${n(counts.desired)} desired · ${n(counts.notSet)} not set`}
            source="From the requirement list"
            action={
              counts.notSet > 0 ? (
                <Link
                  to="/engagements/$engagementId/selection"
                  params={params}
                  search={{ criticality: 'not_set' }}
                  className={STATUS_ROW_LINK}
                >
                  Set criticality
                </Link>
              ) : undefined
            }
          />
          {discoveryOn ? (
            <>
              <StatusRow
                label="Suggestions waiting"
                value={
                  discoveryStarted === false
                    ? 'Not started'
                    : open === null
                      ? suggestions.error
                        ? '—'
                        : '…'
                      : n(open)
                }
                meta={
                  discoveryStarted === false
                    ? 'Suggestions are built once Discovery has started and evidence is confirmed'
                    : open === null && suggestions.error
                      ? 'Suggestions are not available right now'
                      : 'Built from confirmed evidence; added in Selection'
                }
                source="From the suggestion run"
                ai
                action={
                  <Link
                    to="/engagements/$engagementId/selection"
                    params={params}
                    search={{ add: 'discovery' }}
                    className={STATUS_ROW_LINK}
                  >
                    {open !== null && open > 0 ? 'Review' : 'Suggestions'}
                  </Link>
                }
              />
              <StatusRow
                label="Sign-off and packet"
                value={
                  discoveryStarted === false ? (
                    'Not started'
                  ) : signoff.data ? (
                    <StatusChip tone={signoffHeadline(signoff.data).tone} size="sm">
                      {signoffHeadline(signoff.data).chip}
                    </StatusChip>
                  ) : signoff.error ? (
                    'Not available'
                  ) : (
                    '…'
                  )
                }
                meta={
                  discoveryStarted === false
                    ? 'Sign-off opens once Discovery has started'
                    : signoff.data
                      ? signoff.data.export_unlocked
                        ? 'Packet export unlocked'
                        : 'Packet export locked until the client signs or sign-off is waived'
                      : 'The client signs the requirements packet from a secure link'
                }
                source="From the sign-off checklist"
                action={
                  <Link
                    to="/engagements/$engagementId/discovery/signoff"
                    params={params}
                    className={STATUS_ROW_LINK}
                  >
                    Sign-off
                  </Link>
                }
              />
            </>
          ) : null}
        </div>
      )}
    </section>
  );
}
